import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Digital & AI Maturity Report 2026's own landing page — its copy and its
 * figures, apart from the component that draws them.
 *
 * **Why this report has a page instead of the shared report template.** Every
 * other one of the thirty-one reports in `content/reports.ts` is a cover, a
 * paragraph and a download button, and `pages/Reports.tsx` draws all of them the
 * same on purpose. DAIMI is the flagship: on digitopia.co it is a designed
 * landing page — a dark hero over the globe artwork, a counts band, four
 * figures, a framework, a playbook and a closing band — and flattening that into
 * the shared two-column template is what the site already did. Asked for on
 * 2026-08-27 with nine attachments: four figures ("bulabildiğim görseller") and
 * five screenshots of the live page ("aktif sayfanın tasarımı").
 *
 * **The copy here was read off those screenshots, not supplied as text.** That
 * is the whole reason `check` appears on almost every section below. A
 * full-page screenshot of a 3,000px page renders the body copy at about eight
 * pixels, so the *shape* of the page is certain and the *wording and figures*
 * are a careful transcription — and a benchmark report is exactly the kind of
 * document where "2.93" and "2.89" are different claims. `daimiChecks()` feeds
 * these into the same manifest the rest of the site's amber notes use, so a
 * production build cannot ship them unread (see `CheckItem` in
 * `content/checks.ts`).
 *
 * The four figures are the files marketing attached, unretouched:
 * `daimi-hero-globe`, `daimi-report-2026-mockup`, `daimi-maturity-index-gap`,
 * `daimi-framework-wheel` and `daimi-industry-benchmark`.
 */

/** Where every Download button on this page goes — the report itself. */
export const DAIMI_SLUG = 'digital-ai-maturity-report-2026'

/** The page's own headline, set as three lines the way the live hero breaks it. */
export const DAIMI_TITLE = ['Digital & AI', 'Maturity Report', '2026']

export const DAIMI_SUBTITLE = 'Your Guide to Winning the Digital and AI Transformation'

/**
 * The globe backdrop, behind the hero and again behind the closing band.
 *
 * Decorative, so `alt` is empty on purpose: it is the live page's own hero
 * artwork — dark gradient on the left, the wired globe on the right — and it
 * carries no information the headline over it does not already say. A screen
 * reader announcing "dark blue globe with data points" here would be noise
 * between the eyebrow and the headline.
 */
export const DAIMI_HERO_PHOTO: Photo = {
  src: '/photo/daimi-hero-globe.png',
  alt: '',
}

/**
 * The counts band under the hero.
 *
 * Four figures, and the four the live band carries. Read off the screenshot at
 * the top of the page, where they are set large enough to be legible — which is
 * why these are the one set of numbers on this page with no amber note.
 */
export const DAIMI_STATS: { value: string; label: string }[] = [
  { value: '1,575', label: 'assessments' },
  { value: '33,870', label: 'participants' },
  { value: '103,500', label: 'data points' },
  { value: '44', label: 'sub-industries' },
]

export const DAIMI_INTRO = {
  body: [
    'The Digital & AI Maturity Report 2026 is the largest benchmark of its kind, offering a global reality check on how 1,575 organisations are navigating the convergence of digital transformation and AI acceleration.',
    'It gives leaders the evidence-based “north star” they need to replace assumptions with facts, and to act decisively in a rapidly changing landscape.',
  ],
  photo: {
    src: '/photo/daimi-report-2026-mockup.webp',
    alt: 'The Digital & AI Maturity Report 2026, front and back covers',
  } satisfies Photo,
  check:
    'These two paragraphs are transcribed from a screenshot of the live page rather than supplied as text, and they are also what search results will show for this page — `content/reports.ts` takes the meta description from the first of them. Worth reading against the original before production.',
}

export const DAIMI_QUOTE = {
  text: 'As this report shows, understanding where you stand now, and where your competition stands, gives you an understanding of what your “north star” is. AI is changing digital transformation; you, as a leader in your organisation, must change with it.',
  attribution: 'Peter Sondergaard, Chairman, Digitopia',
  check:
    'The Chairman’s quotation is transcribed from a screenshot of the live page rather than supplied as text — please read it against the original word for word before this goes to production, and confirm the attribution line.',
}

export const DAIMI_GAP = {
  heading: 'The Gap’s Widening',
  lede: 'In the last year, AI capability has jumped a generation. Enterprise maturity has moved only marginally.',
  subheading: 'Technology levelled up. Maturity didn’t.',
  body: [
    'Since 2020 we have tracked how organisations progress on digital maturity, and now, with DAIMI, how they progress across digital and AI together.',
    'The 2026 benchmark reveals an uncomfortable truth: technology is accelerating faster than organisations can absorb it. While the market is saturated with AI hype, the benchmark cuts through the noise and shows where organisations actually stand.',
  ],
  photo: {
    src: '/photo/daimi-maturity-index-gap.webp',
    alt: 'Maturity Index by year: DMI rising from 2.70 in 2020 to 3.06 in 2025, DAIMI at 2.93 in 2025, AIMI at 2.30 in 2024 and 2.38 in 2025',
  } satisfies Photo,
  caption: 'Maturity Index, 2020–2025: DMI, DAIMI and AIMI. Source: Digitopia benchmark.',
  check:
    'Two things to confirm on this section: the body copy is transcribed from a screenshot rather than supplied as text, and the figure’s source line is written from the values visible in the chart itself (DMI 2.70→3.06, DAIMI 2.93, AIMI 2.30→2.38) rather than from the credit printed under it on digitopia.co, which was not legible.',
}

/**
 * The six sides of the wheel, in the order the artwork reads them clockwise from
 * the top left. Names only — the wheel carries a short list under each one and
 * none of it was legible, so inventing the sub-items would be inventing the
 * framework.
 */
export const DAIMI_DIMENSIONS = [
  'People',
  'Customer',
  'Operations',
  'Innovation',
  'Governance',
  'Technology',
]

export const DAIMI_FRAMEWORK = {
  heading: 'The DAIMI Framework',
  body: 'DAIMI is Digitopia’s benchmark framework, designed to measure how effectively organisations build capability and turn it into value. Unlike traditional models, it treats digital transformation as no longer sufficient on its own without measurable AI capability beside it.',
  photo: {
    src: '/photo/daimi-framework-wheel-labelled.png',
    alt: 'The DAIMI wheel: People, Customer, Operations, Innovation, Governance and Technology around the Digital & AI Maturity Index',
  } satisfies Photo,
  exploreHref: LINKS.daimi,
  exploreLabel: 'Explore the index',
  check:
    'The framework paragraph is still a transcription from a screenshot of the live page.',
}

export const DAIMI_BENCHMARK = {
  heading: 'The Leaders vs. The Laggards',
  body: 'The 2026 DAIMI benchmark reveals a clear divide: sectors converting AI capability into performance, and sectors still building the foundations. The spread between the top and the bottom performers is what a widening competitive gap looks like.',
  photo: {
    src: '/photo/daimi-industry-benchmark.png',
    alt: 'DAIMI benchmark across seventeen industries, showing the leader, median and laggard score in each',
  } satisfies Photo,
  caption: 'DAIMI 2026 across seventeen industries: leaders, median and laggards.',
  closingHeading: 'Why your industry score matters',
  closingBody:
    'In a market where AI accelerates the advantage of well-run organisations, lagging maturity compounds into a structural disadvantage. Leading industries differentiate themselves through management behaviour: they know their score, they own it, and they improve it.',
  check:
    'The live section states the global average DAIMI score in this paragraph; the figure was not legible in the screenshot, so the sentence has been written without it rather than with a guess. Add it back if it should be there. The industry chart itself is the file supplied (Figure6, 20,010px wide) shown whole.',
}

export const DAIMI_VITALITY = {
  heading: 'The DAIMI Vitality Chart',
  body: [
    'The Vitality chart measures competitive fitness by plotting Digital Velocity against Digital Endurance.',
    'The central takeaway is that unbalanced fitness fails either way: velocity without endurance is fragile, endurance without velocity is stagnant. To succeed, an organisation has to be able to move fast and stay standing.',
  ],
  profiles: ['Iron Man', 'Marathon Runner', 'Sprinter', 'Walker'],
  check:
    'On digitopia.co this section is an interactive scatter chart filtered by country, main industry and company size. It is not reproduced here: these report pages are prerendered with no client bundle at all, so a filtered chart would render once and then sit dead — and neither the data behind it nor an embed URL was supplied. The four profile names are shown without definitions, because which quadrant each one sits in was not legible in the screenshot and placing them wrongly would misstate the model.',
}

export const DAIMI_PLAYBOOK = {
  heading: 'The DAIMI Playbook: turning maturity into value',
  body: 'The path to digital and AI excellence is straightforward and demanding at the same time. It asks leaders to replace assumptions with facts and to execute with accountability. The playbook is the shortest path from activity to impact.',
  steps: ['Assess your maturity', 'Define your strategy', 'Execute your roadmap'],
  close: 'Get the playbook. Download the report today.',
}

export const DAIMI_CLOSING = {
  heading: 'The chasm is opening. Will you be on the right side of it?',
  body: 'The era of “AI experimentation” is over. In 2026 the gap between technology and organisational maturity has become a structural divide, and it can no longer be closed with incremental change. Activity is not progress. Measurable value is.',
  call: ['Don’t just watch the frontier move. Own it.', 'Act with certainty. Execute with discipline. Lead the transformation.'],
  cta: 'Download the 2026 report now',
  check:
    'Two things the live page carries below this band are not here. It closes with a gated HubSpot form — “Gain Exclusive Transformation Insights” — whose portal and form ids were not supplied; and every download on this page goes straight to the PDF in our own bucket instead, which is what the other thirty reports on this site do. Say if the report should be gated behind a form here too.',
}

/**
 * The page's amber notes, in the shape `build-manifest.json` records copy checks
 * in — same mechanism and the same reason as `reportChecks()` next door: a
 * transcription that nobody has read against the original must not slip into
 * production just because it is plausible.
 *
 * `'content'` rather than `'asset'`: none of these is a missing file. Every one
 * is a claim or a figure that belongs to somebody who can confirm it.
 */
export function daimiChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'intro', check: DAIMI_INTRO.check },
    { section: 'quote', check: DAIMI_QUOTE.check },
    { section: 'gap', check: DAIMI_GAP.check },
    { section: 'framework', check: DAIMI_FRAMEWORK.check },
    { section: 'benchmark', check: DAIMI_BENCHMARK.check },
    { section: 'vitality', check: DAIMI_VITALITY.check },
    { section: 'closing', check: DAIMI_CLOSING.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
